import { notification, message } from 'antd';
import moment from 'moment';

export const openNotificationWithIcon = (type, message, description) => {
  notification[type]({
    message,
    description,
  });
};

export const showApiError = (err, fallback = 'Something went wrong') => {
  const msg = (err && err.response && err.response.data && err.response.data.message) || fallback;
  message.error(msg);
};

export const getActiveProfileEmails = ({
  clientProfileData,
  partnerProfileData,
  clientFamilyData,
} = {}) => {
  const memberType = sessionStorage.getItem('memberType');
  const clientprofileid = sessionStorage.getItem('clientprofileid');

  if (memberType === 'spouse' && partnerProfileData && partnerProfileData.emails) {
    return partnerProfileData.emails;
  }
  if (memberType === 'child' && clientFamilyData && clientFamilyData.items) {
    const member = clientFamilyData.items.find((m) => m && m.id == clientprofileid);
    if (member && member.emails) return member.emails;
  }
  return (clientProfileData && clientProfileData.emails) || [];
};

export const getUrlLastId = (value) => {
  const array = value.split('/');
  return array[array.length - 1];
};

export function yyyymmddFormate(date) {
  let dateNew = new Date(date);
  let dd = dateNew.getDate();
  let mm = dateNew.getMonth() + 1;
  let yyyy = dateNew.getFullYear();
  return `${dd < 10 ? `0${dd}` : dd}/${mm < 10 ? `0${mm}` : mm}/${yyyy}`;
}

// ─── Safe date parser/formatter ───────────────────────────────────────────
// Handles every shape the backend can throw at us, including:
//   "1994-02-19"                 — date-only
//   "1994-02-19T00:00:00"        — ISO without zone
//   "1994-02-19T00:00:00.000Z"   — ISO with zone
//   "1994-02-19 00:00:00.0"      — space-separated, single-digit fractional
//                                  (Java/Postgres timestamp serialization —
//                                   moment's strict-ISO parse rejects this)
//   "19/02/1994"                 — pre-formatted day-first (UK/NZ)
//   "02/19/1994"                 — pre-formatted month-first (US)
//   "19-02-1994" / "1994/02/19"  — common variants
//
// Returns the empty string for:
//   - null / undefined / empty input
//   - the 1900-01-01 sentinel ("no date recorded")
//   - any value that won't parse
//
// Always returning a string (never the literal "Invalid date") means it's
// safe to drop directly into JSX without breaking the UI.
export const parseDateSafe = (raw) => {
  if (raw === undefined || raw === null || raw === '') return null;
  const s = String(raw).trim();
  if (!s) return null;
  // Reject the backend's "no date" sentinel(s).
  if (s.indexOf('1900-01-01') === 0 || s.indexOf('0001-01-01') === 0) return null;
  // Strip the time portion (everything after T or space) before strict parse,
  // so "1994-02-19 00:00:00.0" reduces to "1994-02-19" and matches YYYY-MM-DD.
  const datePart = s.split(/[T ]/)[0];
  let m = moment(
    datePart,
    ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD-MM-YYYY', 'MM/DD/YYYY', 'YYYY/MM/DD'],
    true
  );
  if (!m.isValid()) m = moment(s); // lenient fallback on the full original
  if (!m.isValid() || m.year() <= 1900) return null;
  return m;
};

// Formats any backend date shape (see parseDateSafe) as a display string.
// Defaults to DD-MM-YYYY but accepts any moment format token. Returns the
// empty string for unparseable input — never "Invalid date".
export const formatDateSafe = (raw, format = 'DD-MM-YYYY') => {
  const m = parseDateSafe(raw);
  return m ? m.format(format) : '';
};

// DOB-specific convenience: returns DD-MM-YYYY or empty string.
export const formatDobSafe = (raw) => formatDateSafe(raw, 'DD-MM-YYYY');

// ─── Safe date-TIME parser/formatter ──────────────────────────────────────
// Same sentinel/garbage guarding as parseDateSafe, but PRESERVES the time
// component (parseDateSafe strips everything after the "T"/space). Use these
// for timestamps that must show hours/minutes, e.g. file-note "modified" times.
export const parseDateTimeSafe = (raw) => {
  if (raw === undefined || raw === null || raw === '') return null;
  const s = String(raw).trim();
  if (!s) return null;
  // Reject the backend's "no date" sentinel(s).
  if (s.indexOf('1900-01-01') === 0 || s.indexOf('0001-01-01') === 0) return null;
  let m = moment(s, moment.ISO_8601, true);
  if (!m.isValid()) {
    m = moment(s, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm:ss.S', 'YYYY-MM-DDTHH:mm:ss'], true);
  }
  if (!m.isValid()) m = moment(s); // lenient fallback on the full original
  if (!m.isValid() || m.year() <= 1900) return null;
  return m;
};

// Formats any backend datetime shape as a display string, keeping the time.
// Returns the empty string for unparseable/sentinel input — never "Invalid date".
export const formatDateTimeSafe = (raw, format = 'DD MMM YYYY hh:mm A') => {
  const m = parseDateTimeSafe(raw);
  return m ? m.format(format) : '';
};

// True only when a file note was genuinely modified after creation: the
// modified datetime must be valid/non-sentinel AND differ from the created
// datetime. The backend stamps modifiedDate == createdDate on creation, so an
// unedited note must NOT show a "modified" timestamp. Compared at second
// granularity to ignore sub-second serialization noise (a real edit is always
// more than a second after creation).
export const isFileNoteModified = (createdRaw, modifiedRaw) => {
  const modified = parseDateTimeSafe(modifiedRaw);
  if (!modified) return false;
  const created = parseDateTimeSafe(createdRaw);
  if (created && modified.isSame(created, 'second')) return false;
  return true;
};

// Invoice "notes" / message bodies are stored as HTML on the backend (the
// Notes Select option lets users save rich content). Some screens render that
// value into a plain <TextArea>, so raw HTML tags ("<p>...", "<br>", "<strong>",
// "&nbsp;", etc.) leak through and the user sees the markup as literal text.
//
// This helper takes whatever the backend gave us and returns a clean plain-text
// version: block tags collapse to newlines, list items get a leading bullet,
// everything else is stripped, and the common HTML entities are decoded. If the
// input has no '<' at all (already plain text), it's returned as-is.
export const stripHtmlToPlainText = (raw) => {
  if (raw == null) return '';
  const s = String(raw);
  if (s.indexOf('<') === -1) return s;
  let out = s
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\/\s*(p|div|tr|h[1-6])\s*>/gi, '\n')
    .replace(/<\s*li[^>]*>/gi, '• ')
    .replace(/<\/\s*li\s*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'");
  return out.replace(/\n{3,}/g, '\n\n').trim();
};

// Contract templates use `@ClientSignature` (optionally followed by digits
// for multi-signer contracts) as a marker that the live signing page
// (public/AgreementBuilder/index.htm) swaps for an interactive signature
// pad at sign-time. When we also generate a PDF preview of the SAME HTML
// via v1/config/GetPdf, that PDF is static — the marker has nowhere to be
// replaced and shows up as the literal text "@ClientSignature".
//
// Use this helper to transform `@ClientSignature` into a visible "Client
// Signature" placeholder box BEFORE sending the HTML to the PDF generator.
// Apply this ONLY on the PDF path. Do NOT apply it on the contractHTML
// sent to v1/client/contract — that copy must keep the raw marker so the
// AgreementBuilder regex (/(?![^<]+>)@ClientSignature([0-9]*)/gi) still
// finds it and injects the signature widget.
export const transformSignatureMarkers = (html) => {
  if (!html || typeof html !== 'string') return html;
  return html.replace(/(?![^<]+>)@ClientSignature([0-9]*)/gi, (_match, id) => {
    const safeId = (id || '').replace(/[^0-9]/g, '');
    const label = safeId ? `Client Signature ${safeId}` : 'Client Signature';
    return (
      `<div class="ezm-signature-placeholder" data-signature-id="${safeId}"` +
      ` style="display:inline-block;vertical-align:bottom;` +
      `border:1.5px dashed #888;background:#fafafa;` +
      `padding:18px 24px;min-width:260px;min-height:60px;` +
      `margin:12px 0;text-align:center;font-style:italic;` +
      `color:#666;font-size:11px;line-height:1.4;">` +
      `${label}</div>`
    );
  });
};
